import { tolerenceThresholdTestFactory } from '@/domain/units/unit-conversion.utils';
import type { ClassificationNodeId } from '@/interfaces/Classification.interface';
import type { CoreApiPaginatedRequestParams, CoreApiPaginatedResponse } from '@/rosa';
import { SharedUiColorNameV2 } from '@/shared/ui/theming';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { isNotNullish, isNullish } from '@/utils/typing/is-nullish.predicate';
import type { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom, type Observable, Subject } from 'rxjs';
import { catchError, filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { DocumentManagementSystemImportModalComponent } from '../../components/document-management-system-import-modal/document-management-system-import-modal';
import { UnitCode, type UnitConverter } from '../../interfaces/Unit.interface';
import { BrowserProvider } from '../browser.provider';
import { UnitProvider } from '../unit/unit.provider';
import type { ConflictResolutionStrategy, DmsFile, DmsFileId } from './../../interfaces/DocumentManagementSystem.interface';
import { AdvizeoModalService } from './../../services/advizeo-modal.service';
import { ErrorHandlingProvider } from './../error-handling/error-handling.provider';
import { APIError } from './../errors.constants';
import { InformationProvider } from './../information/information.provider';
import {
    AdvizeoCoreDocumentManagementSystemProvider,
    type DeleteFileDto,
    type SearchBodyDto
} from './advizeo-core-document-management-system.provider';

function extractFilenameWithoutExtension(filename: string): string {
    if (isNullish(filename)) return null!;
    return filename.substring(0, filename.lastIndexOf('.'));
}

function extractExtensionFromFilename(filename: string): string {
    if (isNullish(filename)) return null!;
    return filename.substring(filename.lastIndexOf('.') + 1);
}

function checkFileName(filename: string): boolean {
    if (!filename) return false;
    const regexp = /^([À-ÖØ-öø-ÿ\w\s\_\(\).-]+)$/gm;
    return filename.match(regexp) !== null;
}

@Injectable({
    providedIn: 'root'
})
export class DocumentManagementSystemProviderAdapter {
    private _advizeoCoreDmsProvider = inject(AdvizeoCoreDocumentManagementSystemProvider);
    private _advizeoModalService = inject(AdvizeoModalService);
    private _translateService = inject(TranslateService);
    private _informationProvider = inject(InformationProvider);
    private _errorHandlingProvider = inject(ErrorHandlingProvider);
    private _unitProvider = inject(UnitProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _browserProvider = inject(BrowserProvider);

    private _filesChanged = new Subject<ClassificationNodeId | null>();

    notifyFileChange(classificationNodeId: ClassificationNodeId | null) {
        this._filesChanged.next(classificationNodeId);
    }

    getFiles$(
        classificationNodeId: ClassificationNodeId,
        paginationParams: CoreApiPaginatedRequestParams,
        exactName?: string
    ): Observable<CoreApiPaginatedResponse<DmsFile>> {
        return this._filesChanged.pipe(
            filter(impactedClassificationNodeId => !impactedClassificationNodeId || impactedClassificationNodeId === classificationNodeId),
            startWith(null),
            switchMap(() => this._advizeoCoreDmsProvider.fetchFiles$(classificationNodeId, paginationParams, exactName)),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    getFileByName(classificationNodeId: ClassificationNodeId, exactName: string): Promise<DmsFile> {
        return firstValueFrom(
            this.getFiles$(
                classificationNodeId,
                {
                    page: 0,
                    size: 1,
                    sort: []
                },
                exactName
            ).pipe(map(paginatedResponse => (paginatedResponse ? paginatedResponse.content[0] : null!)))
        );
    }

    requestFilesDeletion(
        isAllSelected: boolean,
        includedOrExcludedFileIds: DmsFileId[],
        selectionSize: number,
        classificationNodeId: ClassificationNodeId
    ): void {
        const modal = this._advizeoModalService.openConfirmActionModal({
            title:
                selectionSize === 1
                    ? this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.MASS_DELETE.CONFIRMATION_ONE')
                    : this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.MASS_DELETE.CONFIRMATION', { number: selectionSize }),
            subtitle: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.MASS_DELETE.WARNING'),
            textToType: this._translateService.instant('BUTTONS.DELETE'),
            buttons: [
                {
                    text: this._translateService.instant('BUTTONS.VALIDATE'),
                    code: 'validate',
                    color: SharedUiColorNameV2.PRIMARY
                }
            ]
        });

        const searchBodyDto: SearchBodyDto = {
            classificationNodeId
        };
        const deleteFileDto: DeleteFileDto = {
            isDynamicSelectionEnabled: isAllSelected,
            filters: searchBodyDto,
            filesExcludedFromDynamicSelection: isAllSelected ? includedOrExcludedFileIds : [],
            clientSelectedFileIds: isAllSelected ? [] : includedOrExcludedFileIds
        };

        modal.afterClosed().then(validated => {
            if (validated) {
                this.deleteFiles(deleteFileDto, selectionSize);
            }
        });
    }

    private deleteFiles(deleteFileDto: DeleteFileDto, selectionSize: number) {
        firstValueFrom(
            this._advizeoCoreDmsProvider
                .deleteFiles$(deleteFileDto)
                .pipe(catchError(error => this._errorHandlingProvider.handleAPIError$(error)))
        ).then(() => {
            this.notifyFileChange(null);
            this._informationProvider.showToast(
                selectionSize === 1
                    ? this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.MASS_DELETE.SUCCESS_ONE')
                    : this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.MASS_DELETE.SUCCESS', { number: selectionSize }),
                'success'
            );
        });
    }

    requestFileRenaming(file: DmsFile): void {
        const modal = this._advizeoModalService.openOneFieldModal({
            title: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.RENAME.TITLE'),
            fieldLabel: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.RENAME.NAME_FIELD'),
            fieldType: 'text',
            fieldValue: extractFilenameWithoutExtension(file.fileName),
            submitValidators: [
                {
                    validate: name => checkFileName(name),
                    errorMessage: this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.RENAME.FILENAME_RULES')
                }
            ]
        });

        modal.afterClosed().then(data => {
            if (isNotNullish(data)) {
                this.renameFile(file.fileId, `${data.fieldValue}.${extractExtensionFromFilename(file.fileName)}`);
            }
        });
    }

    private renameFile(fileId: DmsFileId, name: string) {
        firstValueFrom(this._advizeoCoreDmsProvider.updateFile$(fileId, name))
            .then(() => {
                this._informationProvider.showToast(this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.RENAME.SUCCESS'), 'success');
            })
            .catch(errorResponse => {
                if (this.isFileNameAlreadyUsedError(errorResponse)) {
                    this._informationProvider.showToast(
                        this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.FILE_NAME_ALREADY_USED.TITLE'),
                        'error'
                    );
                } else {
                    this._informationProvider.showToast(this._translateService.instant('DOCUMENT_MANAGEMENT_SYSTEM.RENAME.ERROR'), 'error');
                }
            })
            .then(() => {
                this.notifyFileChange(null);
            });
    }

    async importFiles(classificationNodeId: ClassificationNodeId, files: File[]) {
        const remainingQuotaInKB = await firstValueFrom(this.getRemainingFilesQuotaInKB$());
        const totalFileSizeInBytes = files.reduce((total, file) => total + file.size, 0);

        const kBytesToBytes = this._unitProvider.converterFactory(UnitCode.KBYTE, UnitCode.BYTE);

        if (kBytesToBytes(remainingQuotaInKB) < totalFileSizeInBytes) {
            this._informationProvider.showToast('DOCUMENT_MANAGEMENT_SYSTEM.FILES_UPLOAD.NOT_ENOUGH_SPACE_ERROR', 'error');
            return false;
        }
        return await this._advizeoModalService
            .openAdvizeoModal(
                DocumentManagementSystemImportModalComponent,
                {
                    files,
                    classificationNodeId
                },
                { disableClose: true }
            )
            .afterClosed();
    }

    addFile$(
        classificationNodeId: ClassificationNodeId,
        file: File,
        conflictResolutionStrategy: ConflictResolutionStrategy
    ): Observable<unknown> {
        return this._advizeoCoreDmsProvider.uploadFile$(classificationNodeId, file, conflictResolutionStrategy);
    }

    isFileNameAlreadyUsedError(errorResponse: HttpErrorResponse): boolean {
        const apiError: APIError = this._errorHandlingProvider.getErrorCodeFromApiResponse(errorResponse);
        return apiError === APIError.FILE_NAME_ALREADY_USED;
    }

    formatFileSize(fileSize: number, sourceUnitCode: UnitCode): string {
        const displayedUnit = this._unitProvider.getBestConversion(
            [fileSize],
            sourceUnitCode,
            tolerenceThresholdTestFactory(1000, 0.01, 0.1)
        );
        const sizeConverter: UnitConverter = this._unitProvider.converterFactory(sourceUnitCode, displayedUnit);
        const fileSizeInDisplayedUnit = sizeConverter(fileSize);
        const displayedUnitLabel = this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIT, displayedUnit);
        if (displayedUnit === UnitCode.BYTE) return `${fileSizeInDisplayedUnit.toFixed(0)} ${displayedUnitLabel}`;
        else return `${fileSizeInDisplayedUnit.toFixed(1)} ${displayedUnitLabel}`;
    }

    downloadFile(file: DmsFile) {
        this._browserProvider.goTo(file.url);
    }

    getFilesQuota$(): Observable<{ limitInKB: number; currentInKB: number; percent: number }> {
        return this._filesChanged.pipe(
            startWith(null),
            switchMap(() => this._advizeoCoreDmsProvider.fetchQuota$()),
            map(({ limitInKB, currentInKB }) => ({ limitInKB, currentInKB, percent: Math.min((currentInKB * 100) / limitInKB, 100) })),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    getFileChanged$(): Observable<void> {
        return this._filesChanged.pipe(map(() => {}));
    }

    getRemainingFilesQuotaInKB$(): Observable<number> {
        return this.getFilesQuota$().pipe(map(({ limitInKB, currentInKB }) => limitInKB - currentInKB));
    }
}
